import React, { useState, useEffect, useRef, memo } from 'react'
import Button from 'components/button'
import { ICommentThreadObject, ICommentObject, CommentThreadStatusType, ActionMenuType } from 'types'
import { useApplicationStore } from 'hooks/application'
import CommentUser from 'components/comments/commentUserDetails'
import ReplyCommentComponent from 'components/comments/replyComment'
import api from 'helpers/api'
import CommentActionsMenu from 'components/comments/commentActionsMenu'
import { dateTime } from 'helpers/date'
import { CollapseAll, ExpandAll, Tick, Comment as CommentIcon } from 'components/icons'
import { ISpreadsheetData } from 'components/spreadsheet/types'
import useProject from 'hooks/useProject'
import CommentEditor from 'components/comments/commentEditor'
import CommentBody from 'components/comments/commentBody'
import { MentionMember, editorTextToMarkdown } from 'helpers/mentions'

interface ICommentThreadActionsButtonsProps {
  action: ActionMenuType | null
  disabled: boolean
  processing: boolean
  onYes: (action: ActionMenuType) => void
  onCancelButton: () => void
}

const CommentThreadActionsButtons: React.FC<ICommentThreadActionsButtonsProps> = ({
  action,
  disabled,
  processing,
  onYes,
  onCancelButton
}) => {
  const message = action === 'edit' ? 'this comment?' : 'this comments thread'
  return (
    <div className="block m-[5px]">
      {action === 'delete' && (
        <div>
          <span className="h-5"> Are you sure you want to </span>
          <span className="bold h-5">{action}</span> <span>{message}</span>
        </div>
      )}
      <div className="inline-flex mt-[5px] mb-2.5 mx-0">
        <Button className="mr-[5px] min-w-20" onClick={() => onYes(action)} disabled={disabled} isLoading={processing}>
          {action === 'edit' ? 'Update' : 'Yes'}
        </Button>
        <Button className="mr-[5px] w-20" internalType="danger" onClick={() => onCancelButton()} disabled={disabled}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

interface ICommentThreadReplyActionsButtonsProps {
  disabled: boolean
  processing: boolean
  onReply: () => void
}
const CommentThreadReplyActionsButtons: React.FC<ICommentThreadReplyActionsButtonsProps> = ({
  disabled,
  processing,
  onReply
}) => {
  return (
    <div className="inline-flex mt-[5px] mb-2.5 mx-0">
      <Button className="mr-[5px] min-w-20" onClick={() => onReply()} disabled={disabled} isLoading={processing}>
        Reply
      </Button>
    </div>
  )
}

export interface ICommentsThreadProps {
  projectId: string
  intialCommentThread: ICommentThreadObject
  selected: boolean
  index: number
  permissionLevel: number
  members: MentionMember[]
  spreadsheetData?: ISpreadsheetData
  onSelected: (public_id: string) => void
  onCommentThreadChange: (index: number, commentThread: ICommentThreadObject) => void
}
// Comments render as a flat, divided list — no cards, shadows or rounded
// corners. Threads are separated from one another by a hairline rule (added by
// the caller for every thread after the first). The selected thread gets a
// subtle blue tint; resolved threads are muted.
const threadClasses = (selected: boolean, resolved: boolean): string => {
  const base = 'flex flex-col gap-2 p-3 transition-colors'
  if (resolved) return `${base} opacity-75`
  if (selected) return `${base} bg-really-light-blue`
  return base
}

const CommentsThreadComponent: React.FC<ICommentsThreadProps> = ({
  projectId,
  intialCommentThread,
  selected,
  index,
  permissionLevel,
  members,
  spreadsheetData,
  onSelected,
  onCommentThreadChange
}) => {
  const threadRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('commentThread') !== intialCommentThread.publicId) return
    const frame = requestAnimationFrame(() => {
      const commentId = params.get('comment')
      const target = Array.from(threadRef.current?.querySelectorAll<HTMLElement>('[data-comment-id]') ?? []).find(
        (element) => element.dataset.commentId === commentId
      )
      ;(target ?? threadRef.current)?.scrollIntoView({ block: 'nearest' })
    })
    return () => cancelAnimationFrame(frame)
  }, [intialCommentThread.publicId])
  const { projectOwner, projectAdmin } = useProject()
  const [commentThread, setCommentThread] = useState<ICommentThreadObject>(intialCommentThread)
  const [mainCommentText, setMainCommentText] = useState('')
  const [replyCommentText, setReplyCommentText] = useState('')
  const [readOnly, setReadOnly] = useState(true)
  const [selectedAction, setSelectedAction] = useState<ActionMenuType>(null)
  const { displayErrorMessage } = useApplicationStore()
  const [processing, setProcessing] = useState<boolean>(false)
  const [processingReply, setProcessingReply] = useState<boolean>(false)
  const [showReplyComments, setShowReplyComments] = useState<boolean>(true)

  useEffect(() => {
    if (!selected) {
      setReplyCommentText('')
    }
  }, [selected])

  useEffect(() => {
    onCommentThreadChange(index, commentThread)
    if (commentThread.resolvedAt) {
      setReadOnly(true)
      setSelectedAction(null)
      setMainCommentText('')
    }
  }, [commentThread])

  const handleStatusCommentThread = (status: CommentThreadStatusType) => {
    let additionalContext = {}
    if (spreadsheetData) {
      additionalContext = {
        processPublicId: spreadsheetData.processId,
        processSectionPublicId: spreadsheetData.processSectionId
      }
    }

    const context = { context: { projectId, ...additionalContext } }

    api
      .updateStatusCommentThread(context, commentThread!.publicId, status)
      .then((response) => {
        setCommentThread(response.data)
        setReplyCommentText('')
      })
      .catch((error) => {
        displayErrorMessage(error)
      })
  }

  const handleOnSelectedActionButton = (action: ActionMenuType, comment: ICommentObject) => {
    setProcessing(true)
    if (action === 'edit') {
      const commentText = editorTextToMarkdown(mainCommentText, members)
      const originalEditable = comment.commentText
      if (mainCommentText === originalEditable) {
        setSelectedAction(null)
        setProcessing(false)
        setReadOnly(true)
        setMainCommentText('')
        return
      }
      let additionalContext = {}
      if (spreadsheetData) {
        additionalContext = {
          processPublicId: spreadsheetData.processId,
          processSectionPublicId: spreadsheetData.processSectionId
        }
      }
      api
        .updateComment(commentThread.publicId, comment.publicId, {
          commentText: commentText,
          context: { projectId, ...additionalContext }
        })
        .then((response) => {
          const comments = [...commentThread.comments]
          comments[0] = response.data
          setCommentThread({ ...commentThread, comments: comments })
          setSelectedAction(null)
          setProcessing(false)
          setReadOnly(true)
        })
        .catch((error) => {
          displayErrorMessage(error)
          setProcessing(false)
        })
    } else if (action === 'delete') {
      let additionalContext = {}
      if (spreadsheetData) {
        additionalContext = {
          processPublicId: spreadsheetData.processId,
          processSectionPublicId: spreadsheetData.processSectionId
        }
      }

      const context = { context: { projectId, ...additionalContext } }
      api
        .deleteCommentThread(context, commentThread.publicId)
        .then((response) => {
          setCommentThread(response.data)
          setSelectedAction(null)
          setProcessing(false)
        })
        .catch((error) => {
          displayErrorMessage(error)
          setProcessing(false)
        })
    }
  }

  const handleOnReply = () => {
    setProcessingReply(true)
    let additionalContext = {}
    if (spreadsheetData) {
      additionalContext = {
        processPublicId: spreadsheetData.processId,
        processSectionPublicId: spreadsheetData.processSectionId
      }
    }
    const commentText = editorTextToMarkdown(replyCommentText, members)
    const data = { commentText, context: { projectId, ...additionalContext } }
    api
      .postComment(commentThread.publicId, data)
      .then((response) => {
        setCommentThread({ ...commentThread, comments: [...commentThread.comments, response.data] })
        setProcessingReply(false)
        setReplyCommentText('')
      })
      .catch((error) => {
        displayErrorMessage(error)
        setProcessingReply(false)
      })
  }

  const handleOnDisplayReplyComments = () => {
    setShowReplyComments(!showReplyComments)
    setReplyCommentText('')
  }

  const hadleOnReplyCommentChange = (index: number, comment: ICommentObject) => {
    const updatedComments = [...commentThread.comments]
    updatedComments[index] = comment
    const commentsToUpdate = updatedComments.filter((comment) => !comment.deletedAt)
    setCommentThread({ ...commentThread, comments: commentsToUpdate })
  }

  const handleOnSelectedActionMenu = (action: ActionMenuType) => {
    setSelectedAction(action)
    if (action === 'edit') {
      const mainComment = commentThread.comments[0]
      setMainCommentText(mainComment.commentText)
      setReadOnly(false)
    } else {
      setReadOnly(true)
      setMainCommentText('')
    }
  }

  const handleCancelButton = () => {
    setSelectedAction(null)
    setMainCommentText('')
    setReadOnly(true)
  }

  if (!commentThread) {
    return null
  }

  /**
   * First comment
   */
  const mainComment = commentThread!.comments[0]
  /**
   * Remaining comments are replies to the main comment
   */
  const replyComments = [...commentThread!.comments]
  replyComments.shift()

  const displayShowHideButton = !commentThread.resolvedAt || (commentThread.resolvedAt && replyComments.length > 0)

  return (
    <div ref={threadRef} className={index > 0 ? 'border-t border-divider' : ''}>
      <div
        data-comment-id={mainComment.publicId}
        className={threadClasses(selected, Boolean(commentThread.resolvedAt))}
        onClick={() => onSelected(commentThread.publicId)}
      >
        <div className="flex justify-between w-full items-center gap-2 flex-wrap">
          <CommentUser comment={mainComment} />
          {commentThread.resolvedAt && (
            <span className="text-xs font-medium text-dark-green bg-light-green rounded-full px-2 py-0.5">
              Resolved
            </span>
          )}
          <div className="inline-flex ml-auto">
            {(commentThread.isCommentInitiator || permissionLevel == 4 || projectAdmin || projectOwner) && (
              <div
                className="cursor-pointer flex items-center p-1.5 rounded text-muted hover:bg-surface-sunken hover:text-body transition-colors"
                title={commentThread.resolvedAt ? `Re-open` : 'Resolve'}
                onClick={() => handleStatusCommentThread(commentThread.resolvedAt ? `reopen` : 'resolve')}
              >
                {commentThread.resolvedAt ? <CommentIcon /> : <Tick />}
              </div>
            )}

            {displayShowHideButton && (
              <div
                className="cursor-pointer flex items-center p-1.5 rounded text-muted hover:bg-surface-sunken hover:text-body transition-colors"
                title={showReplyComments ? `Hide` : 'Show'}
                onClick={() => handleOnDisplayReplyComments()}
              >
                {showReplyComments ? <CollapseAll /> : <ExpandAll />}
              </div>
            )}
            {commentThread.isCommentInitiator && !commentThread.resolvedAt && (
              <CommentActionsMenu
                onSelectedActionMenu={(action: ActionMenuType) => handleOnSelectedActionMenu(action)}
              />
            )}
          </div>
        </div>
        {!readOnly ? (
          <CommentEditor
            id={`main-comment-${mainComment.publicId}`}
            value={mainCommentText}
            onChange={setMainCommentText}
            members={members}
            focusOnMount
          />
        ) : (
          <div className="bg-surface select-text break-words p-2.5 rounded-lg">
            <CommentBody markdown={mainComment.commentText} members={members} />
          </div>
        )}
        {selectedAction && (
          <CommentThreadActionsButtons
            action={selectedAction}
            onYes={(action: ActionMenuType) => handleOnSelectedActionButton(action, mainComment)}
            onCancelButton={() => handleCancelButton()}
            disabled={processing}
            processing={processing}
          />
        )}
        {showReplyComments && replyComments && (
          <div>
            <div className="ml-3 mt-1 flex flex-col">
              {replyComments.map((comment: ICommentObject, index: number) => {
                const reply_index = index + 1 // index =0 is the parent comment. Replies starts on index = 1
                const isLastReply = index === replyComments.length - 1
                return (
                  <div
                    data-comment-id={comment.publicId}
                    key={`reply-comment-${comment.publicId}`}
                    className={`relative pl-6 before:content-[''] before:absolute before:left-1 before:top-0 ${
                      isLastReply ? 'before:h-1' : 'before:bottom-0'
                    } before:w-px before:bg-surface-strong after:content-[''] after:absolute after:left-1 after:top-1 after:h-5 after:w-5 after:border-l after:border-b after:border-divider after:rounded-bl-xl`}
                  >
                    <ReplyCommentComponent
                      projectId={projectId}
                      commentThreadId={commentThread.publicId}
                      initialComment={comment}
                      index={reply_index}
                      onCommentChange={(reply_index, comment) => hadleOnReplyCommentChange(reply_index, comment)}
                      isResolvedCommentThread={!!commentThread.resolvedAt}
                      members={members}
                      spreadsheetData={spreadsheetData}
                    />
                  </div>
                )
              })}
            </div>

            {!commentThread.resolvedAt && (
              <div className="flex items-center flex-col mt-3 py-[5px] px-0">
                <CommentEditor
                  id={`reply-comment-${mainComment.publicId}`}
                  value={replyCommentText}
                  onChange={setReplyCommentText}
                  members={members}
                  placeholder="Add a reply..."
                />
                <div className="flex w-full items-center justify-end">
                  <CommentThreadReplyActionsButtons
                    disabled={processingReply || !replyCommentText}
                    processing={processingReply}
                    onReply={() => handleOnReply()}
                  />
                </div>
              </div>
            )}
          </div>
        )}
        {commentThread.resolvedAt && commentThread.resolver && (
          <span>
            Resolved by {commentThread.resolver.name} on {dateTime(new Date(commentThread.resolvedAt)).toLocaleString()}
          </span>
        )}
      </div>
    </div>
  )
}

function areEqualCommentThread(prevCommentThread: ICommentThreadObject, nextCommentThread: ICommentThreadObject) {
  return (
    prevCommentThread.deletedAt === nextCommentThread.deletedAt &&
    prevCommentThread.resolvedAt === nextCommentThread.resolvedAt &&
    prevCommentThread.updatedAt === nextCommentThread.updatedAt &&
    prevCommentThread.comments.length === nextCommentThread.comments.length
  )
}

function areEqual(prevProps: ICommentsThreadProps, nextProps: ICommentsThreadProps) {
  return (
    prevProps.selected === nextProps.selected &&
    prevProps.index === nextProps.index &&
    prevProps.permissionLevel === nextProps.permissionLevel &&
    // members load asynchronously; without this the thread won't re-render to
    // resolve mention names when they arrive.
    prevProps.members === nextProps.members &&
    areEqualCommentThread(prevProps.intialCommentThread, nextProps.intialCommentThread)
  )
}

export default memo(CommentsThreadComponent, areEqual)
